import { useState, useEffect } from 'react';
import { collection, addDoc, query, getDocs, where, orderBy, updateDoc, doc, writeBatch, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from '../../hooks/useTranslation';
import isEqual from 'lodash/isEqual';
import { exportToJson, exportToCsv } from '../../utils/exportHelpers';
import BatchOperationsManager from './BatchOperationsManager';
import { logAdminAction } from '../../utils/auditLogger';
import AddWalletBalance from './AddWalletBalance';
import CreateWalletUser from './CreateWalletUser';


export default function WalletUserManager() {
  const { t } = useTranslation();
  const [walletUsers, setWalletUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    skipped: 0,
    failed: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTransactions, setUserTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionsPerPage] = useState(20);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [selectedCoin, setSelectedCoin] = useState('FLCV');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  const [transferDescription, setTransferDescription] = useState('');

  useEffect(() => {
    fetchWalletUsers();
  }, []);

  const fetchWalletUsers = async () => {
    try {
      const walletUsersRef = collection(db, 'walletUsers');
      const q = query(walletUsersRef, orderBy('email'));
      const snapshot = await getDocs(q);
      
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setWalletUsers(users);
    } catch (error) {
      console.error('Error fetching wallet users:', error);
      toast.error('Hiba történt a wallet felhasználók betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setStats({
      total: 0,
      processed: 0,
      successful: 0,
      skipped: 0,
      failed: 0
    });

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const users = data.users || [];
      
      setStats(prev => ({ ...prev, total: users.length }));

      for (const [index, user] of users.entries()) {
        try {
          const walletUserData = {
            email: user.email || '',
            ip: user.ip || '',
            email_verified: user.email_verified || false,
            document_verified: user.document_verified || false,
            user_id: user.user_id || '',
            details: {
              first_name: user.details?.first_name || '',
              last_name: user.details?.last_name || '',
              business_name: user.details?.business_name || '',
              country: user.details?.country || '',
              city: user.details?.city || '',
              zip_code: user.details?.zip_code || '',
              address: user.details?.address || '',
              account_type: user.details?.account_type || '',
              status: user.details?.status || '',
              balances: user.details?.balances || {},
              account_level: user.details?.account_level || '',
              account_user: user.details?.account_user || ''
            },
            activities: user.activities || [],
            last_updated: new Date()
          };

          // Ellenőrizzük, hogy létezik-e már ez a felhasználó
          const existingUserQuery = query(
            collection(db, 'walletUsers'), 
            where('email', '==', walletUserData.email)
          );
          const existingUserDocs = await getDocs(existingUserQuery);

          if (existingUserDocs.empty) {
            // Ha nem létezik, új felhasználót hozunk létre
            walletUserData.created_at = new Date();
            await addDoc(collection(db, 'walletUsers'), walletUserData);
            setStats(prev => ({
              ...prev,
              processed: index + 1,
              successful: prev.successful + 1
            }));
          } else {
            // Ha létezik, ellenőrizzük a változásokat
            const existingDoc = existingUserDocs.docs[0];
            const existingData = existingDoc.data();
            
            // Összehasonlítjuk a meglévő és új adatokat
            const hasChanges = !isEqual(existingData.details, walletUserData.details) || 
                             existingData.email_verified !== walletUserData.email_verified ||
                             existingData.document_verified !== walletUserData.document_verified;

            if (hasChanges) {
              // Csak akkor frissítünk, ha van változás
              await updateDoc(existingDoc.ref, {
                details: walletUserData.details,
                email_verified: walletUserData.email_verified,
                document_verified: walletUserData.document_verified,
                last_updated: new Date()
              });
              setStats(prev => ({
                ...prev,
                processed: index + 1,
                successful: prev.successful + 1
              }));
            } else {
              // Ha nincs változás, csak növeljük a feldolgozott számot
              setStats(prev => ({
                ...prev,
                processed: index + 1,
                skipped: (prev.skipped || 0) + 1
              }));
            }
          }

        } catch (error) {
          console.error('Error importing wallet user:', error);
          setStats(prev => ({
            ...prev,
            processed: index + 1,
            failed: prev.failed + 1
          }));
        }
      }

      // Módosítsuk a statisztika megjelenítését
      toast.success(`Importálás befejezve: 
        ${stats.successful} sikeres, 
        ${stats.skipped || 0} kihagyott (nem változott), 
        ${stats.failed} sikertelen`
      );

    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Hiba történt a fájl feldolgozása során');
    } finally {
      setImporting(false);
    }
  };

  const handleSingleUserImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const userData = JSON.parse(text);
      
      setImporting(true);
      setStats({
        total: 1,
        processed: 0,
        successful: 0,
        skipped: 0,
        failed: 0
      });

      const walletUserData = {
        email: userData.details?.email || '',
        user_id: userData.user_id || '',
        email_verified: userData.details?.email_verified || false,
        document_verified: userData.details?.document_verified || false,
        details: {
          first_name: userData.details?.first_name || '',
          last_name: userData.details?.last_name || '',
          business_name: userData.details?.business_name || '',
          country: userData.details?.country || '',
          city: userData.details?.city || '',
          zip_code: userData.details?.zip_code || '',
          address: userData.details?.address || '',
          account_type: userData.details?.account_type || '',
          status: userData.details?.status || '',
          balances: userData.details?.balances || {},
          account_level: userData.details?.account_level || '',
          account_user: userData.details?.account_user || ''
        },
        activities: (userData.activities || [])
          .slice(0, 10)
          .map(activity => ({
            date: activity.date,
            txid: activity.txid,
            activity: activity.activity,
            amount: activity.amount,
            status: activity.status
          })),
        last_updated: new Date(),
        created_at: new Date()
      };

      // Ellenőrizzük, hogy létezik-e már ez a felhasználó
      const existingUserQuery = query(
        collection(db, 'walletUsers'), 
        where('user_id', '==', walletUserData.user_id)
      );
      const existingUserDocs = await getDocs(existingUserQuery);

      if (existingUserDocs.empty) {
        walletUserData.created_at = new Date();
        await addDoc(collection(db, 'walletUsers'), walletUserData);
        setStats(prev => ({
          ...prev,
          processed: 1,
          successful: 1
        }));
        
        await logAdminAction('user.single_import', {
          user_id: walletUserData.user_id,
          email: walletUserData.email,
          type: 'create'
        });
        
        toast.success('Felhasználó sikeresen importálva');
      } else {
        const existingDoc = existingUserDocs.docs[0];
        const existingData = existingDoc.data();
        
        const hasChanges = !isEqual(existingData.details, walletUserData.details);

        if (hasChanges) {
          await updateDoc(existingDoc.ref, {
            details: walletUserData.details,
            email_verified: walletUserData.email_verified,
            document_verified: walletUserData.document_verified,
            last_updated: new Date()
          });
          
          setStats(prev => ({
            ...prev,
            processed: 1,
            successful: 1
          }));
          
          await logAdminAction('user.single_import', {
            user_id: walletUserData.user_id,
            email: walletUserData.email,
            type: 'update'
          });
          
          toast.success('Felhasználó sikeresen frissítve');
        } else {
          setStats(prev => ({
            ...prev,
            processed: 1,
            skipped: 1
          }));
          toast.info('Nincs változás a felhasználó adataiban');
        }
      }

      fetchWalletUsers();
    } catch (error) {
      console.error('Error importing single user:', error);
      toast.error('Hiba történt a felhasználó importálása során');
      setStats(prev => ({
        ...prev,
        processed: 1,
        failed: 1
      }));
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  const filteredUsers = walletUsers.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.details?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.details?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchUserTransactions = async (userId, page = 1) => {
    setLoadingTransactions(true);
    try {
      const transactionsRef = collection(db, 'walletTransactions');
      const q = query(
        transactionsRef,
        where('sender.user_id', '==', userId.toString()),
        orderBy('created_at', 'desc'),
        limit(transactionsPerPage)
      );
      const receivedQ = query(
        transactionsRef,
        where('recipient.user_id', '==', userId.toString()),
        orderBy('created_at', 'desc'),
        limit(transactionsPerPage)
      );

      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(q),
        getDocs(receivedQ)
      ]);

      const sentTxs = sentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'sent'
      }));

      const receivedTxs = receivedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'received'
      }));

      const combinedTxs = [...sentTxs, ...receivedTxs].sort((a, b) => 
        b.created_at?.toDate?.() - a.created_at?.toDate?.()
      );

      setHasMoreTransactions(combinedTxs.length === transactionsPerPage * 2);
      
      if (page === 1) {
        setUserTransactions(combinedTxs);
      } else {
        setUserTransactions(prev => [...prev, ...combinedTxs]);
      }
      
      setTransactionPage(page);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      toast.error('Hiba történt a tranzakciók betöltésekor');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleExportJson = () => {
    const exportData = walletUsers.map(user => ({
      email: user.email,
      user_id: user.user_id,
      details: user.details,
      balances: user.details?.balances || {},
      status: user.details?.status,
      created_at: user.created_at?.toDate?.().toISOString(),
      last_updated: user.last_updated?.toDate?.().toISOString()
    }));
    
    exportToJson(exportData, `wallet_users_${new Date().toISOString()}.json`);
  };

  const handleExportCsv = () => {
    const exportData = walletUsers.map(user => ({
      email: user.email,
      user_id: user.user_id,
      first_name: user.details?.first_name,
      last_name: user.details?.last_name,
      country: user.details?.country,
      city: user.details?.city,
      status: user.details?.status,
      balances: JSON.stringify(user.details?.balances || {}),
      created_at: user.created_at?.toDate?.().toISOString(),
      last_updated: user.last_updated?.toDate?.().toISOString()
    }));
    
    exportToCsv(exportData, `wallet_users_${new Date().toISOString()}.csv`);
  };

  const handleRevertTransaction = async (transaction) => {
    if (!window.confirm('Biztosan visszavonja ezt a tranzakciót?')) {
      return;
    }

    try {
      // 1. Új revert tranzakció létrehozása (ellentétes irányú)
      const revertData = {
        transaction_id: `REVERT_${transaction.transaction_id}`,
        amount: transaction.amount,
        sender: transaction.recipient,
        recipient: transaction.sender,
        created_at: new Date(),
        status: 'completed',
        type: 'revert',
        original_transaction: transaction.id,
        coin: transaction.amount.split(' ')[1]
      };

      // 2. Egyenlegek visszaállítása
      const amount = parseFloat(transaction.amount);
      const coin = transaction.amount.split(' ')[1];

      // Lekérjük a jelenlegi felhasználókat
      const senderRef = query(
        collection(db, 'walletUsers'),
        where('user_id', '==', transaction.recipient.user_id)
      );
      const recipientRef = query(
        collection(db, 'walletUsers'),
        where('user_id', '==', transaction.sender.user_id)
      );

      const [senderSnapshot, recipientSnapshot] = await Promise.all([
        getDocs(senderRef),
        getDocs(recipientRef)
      ]);

      const senderDoc = senderSnapshot.docs[0];
      const recipientDoc = recipientSnapshot.docs[0];

      // Batch használata
      const batch = writeBatch(db);

      // Eredeti tranzakció megjelölése
      batch.update(doc(db, 'walletTransactions', transaction.id), {
        reverted: true,
        reverted_at: new Date()
      });

      // Új revert tranzakció létrehozása
      const revertRef = doc(collection(db, 'walletTransactions'));
      batch.set(revertRef, revertData);

      // Egyenlegek frissítése
      if (senderDoc) {
        const currentBalance = parseFloat(senderDoc.data().details?.balances?.[coin] || 0);
        batch.update(senderDoc.ref, {
          [`details.balances.${coin}`]: (currentBalance - amount).toString()
        });
      }

      if (recipientDoc) {
        const currentBalance = parseFloat(recipientDoc.data().details?.balances?.[coin] || 0);
        batch.update(recipientDoc.ref, {
          [`details.balances.${coin}`]: (currentBalance + amount).toString()
        });
      }

      await batch.commit();

      toast.success('Tranzakció sikeresen visszavonva');
      fetchUserTransactions(selectedUser.user_id); // Frissítjük a tranzakciók listáját
      fetchWalletUsers(); // Frissítjük a felhasználók listáját az új egyenlegekkel

    } catch (error) {
      console.error('Error reverting transaction:', error);
      toast.error('Hiba történt a tranzakció visszavonása során');
    }
  };

  const searchRecipients = async (term) => {
    if (term.length < 3) {
      setRecipientResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'walletUsers');
      const q = query(
        usersRef,
        where('email', '>=', term),
        where('email', '<=', term + '\uf8ff'),
        limit(5)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => user.user_id !== selectedUser.user_id); // Kizárjuk a kiválasztott felhasználót
      setRecipientResults(users);
    } catch (error) {
      console.error('Error searching recipients:', error);
    }
  };

  const handleTransfer = async () => {
    if (!selectedRecipient || !transferAmount || !selectedCoin) {
      toast.error('Kérjük, töltse ki az összes mezőt');
      return;
    }

    try {
      const transactionData = {
        transaction_id: `ADMIN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount: `${transferAmount} ${selectedCoin}`,
        sender: {
          email: selectedUser.email,
          user_id: selectedUser.user_id
        },
        recipient: {
          email: selectedRecipient.email,
          user_id: selectedRecipient.user_id
        },
        created_at: new Date(),
        status: 'completed',
        type: 'admin_transfer',
        description: transferDescription.trim() || null
      };

      const batch = writeBatch(db);

      // Tranzakció létrehozása
      const txRef = doc(collection(db, 'walletTransactions'));
      batch.set(txRef, transactionData);

      // Egyenlegek frissítése
      const senderBalance = parseFloat(selectedUser.details?.balances?.[selectedCoin] || 0);
      const recipientBalance = parseFloat(selectedRecipient.details?.balances?.[selectedCoin] || 0);
      const amount = parseFloat(transferAmount);

      // Küldő egyenlegének frissítése
      const senderRef = doc(db, 'walletUsers', selectedUser.id);
      batch.update(senderRef, {
        [`details.balances.${selectedCoin}`]: (senderBalance - amount).toString(),
        last_updated: new Date()
      });

      // Fogadó egyenlegének frissítése
      const recipientRef = doc(db, 'walletUsers', selectedRecipient.id);
      batch.update(recipientRef, {
        [`details.balances.${selectedCoin}`]: (recipientBalance + amount).toString(),
        last_updated: new Date()
      });

      await batch.commit();

      // Log the transfer
      await logAdminAction('user.transfer', {
        sender: selectedUser.user_id,
        recipient: selectedRecipient.user_id,
        amount: transferAmount,
        coin: selectedCoin
      });

      toast.success('Utalás sikeresen végrehajtva');
      setShowTransferModal(false);
      setTransferAmount('');
      setSelectedRecipient(null);
      setTransferDescription('');
      fetchUserTransactions(selectedUser.user_id);
      fetchWalletUsers();
    } catch (error) {
      console.error('Error executing transfer:', error);
      toast.error('Hiba történt az utalás során');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet Felhasználók</h1>
        <p className="mt-1 text-sm text-gray-500">Importált wallet felhasználók kezelése</p>
      </div>

      {/* Import és Keresés */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Keresés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
            <Icons.Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Icons.UserPlus className="h-5 w-5" />
            <span className="text-sm font-medium">Új Felhasználó</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportJson}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Icons.Download className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Export JSON</span>
            </button>
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Icons.FileText className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Export CSV</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <Icons.Upload className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Bulk Import</span>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <Icons.UserPlus className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Single Import</span>
              <input
                type="file"
                accept=".json"
                onChange={handleSingleUserImport}
                className="hidden"
              />
            </label>
          </div>
          <div className="text-sm text-gray-500">
            {walletUsers.length} felhasználó
          </div>
        </div>
      </div>

      {/* Felhasználók listája */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={currentUsers.length > 0 && selectedUsers.length === currentUsers.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(currentUsers);
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Felhasználó
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Elérhetőség
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Egyenleg
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.some(u => u.id === user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
                        }
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-500">
                            {user.details?.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.details?.first_name} {user.details?.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {user.user_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    <div className="text-sm text-gray-500">{user.details?.city}, {user.details?.country}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {Object.entries(user.details?.balances || {}).map(([currency, amount]) => (
                      <div key={currency} className="text-sm text-gray-900">
                        {amount} {currency}
                      </div>
                    ))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${user.details?.status === '1' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.details?.status === '1' ? 'Aktív' : 'Inaktív'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2 justify-end">
                      <button 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDetailsModal(true);
                          fetchUserTransactions(user.user_id);
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Icons.Eye className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowBalanceModal(true);
                        }}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Icons.Wallet className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex space-x-1">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first page, last page, current page, and pages around current
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNumber
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronRight className="h-5 w-5" />
          </button>

          <div className="text-sm text-gray-500">
            {t('common.showing', {
              from: indexOfFirstItem + 1,
              to: Math.min(indexOfLastItem, filteredUsers.length),
              total: filteredUsers.length
            })}
          </div>
        </div>
      )}

      {importing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full">
            <h3 className="text-xl font-medium text-gray-900 mb-6">Importálás folyamatban...</h3>
            
            <div className="mb-6">
              <div className="h-3 bg-gray-200 rounded-full">
                <div 
                  className="h-3 bg-indigo-600 rounded-full transition-all duration-300"
                  style={{ width: `${(stats.processed / stats.total) * 100}%` }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-gray-500 mb-2">Feldolgozva:</p>
                <p className="font-medium text-lg">{stats.processed} / {stats.total}</p>
              </div>
              <div className="space-y-2">
                <p className="text-green-600 font-medium">Sikeres: {stats.successful}</p>
                <p className="text-blue-600 font-medium">Kihagyott: {stats.skipped}</p>
                <p className="text-red-600 font-medium">Sikertelen: {stats.failed}</p>
              </div>
            </div>
            
            <div className="mt-6 text-sm text-gray-500 font-medium text-center">
              {Math.round((stats.processed / stats.total) * 100)}% kész
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-medium text-gray-900">
                  Felhasználó részletek
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedUser.email}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="px-5 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                >
                  <Icons.Send className="h-4 w-4 mr-2" />
                  Új utalás
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedUser(null);
                    setUserTransactions([]);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <Icons.X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* User Details */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-4">
                  Személyes adatok
                </h4>
                <div className="space-y-3">
                  <p className="text-sm">
                    <span className="font-medium">Név:</span>{' '}
                    {selectedUser.details?.first_name} {selectedUser.details?.last_name}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Ország:</span>{' '}
                    {selectedUser.details?.country}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Város:</span>{' '}
                    {selectedUser.details?.city}
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-4">
                  Egyenlegek
                </h4>
                <div className="space-y-3">
                  {Object.entries(selectedUser.details?.balances || {}).map(([currency, amount]) => (
                    <p key={currency} className="text-sm">
                      <span className="font-medium">{currency}:</span>{' '}
                      <span className="text-indigo-600 font-medium">{amount}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Transactions */}
            <div>
              <h4 className="text-base font-medium text-gray-700 mb-4">
                Tranzakciók
              </h4>
              {loadingTransactions ? (
                <div className="flex justify-center py-6">
                  <Icons.Loader className="animate-spin h-8 w-8 text-indigo-500" />
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Tranzakció ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Típus
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Partner
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Összeg
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Leírás
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Dátum
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Műveletek
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userTransactions.map((tx) => (
                        <tr key={tx.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {tx.transaction_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              tx.type === 'sent' 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {tx.type === 'sent' ? 'Küldött' : 'Fogadott'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {tx.type === 'sent' ? tx.recipient?.email : tx.sender?.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {tx.amount}
                          </td>
                          <td className="px-6 py-4 whitespace-pre-wrap text-sm text-gray-500 max-w-xs">
                            {editingDescription?.id === tx.id ? (
                              <div className="flex gap-2">
                                <textarea
                                  value={editingDescription.text}
                                  onChange={(e) => setEditingDescription({
                                    id: tx.id,
                                    text: e.target.value
                                  })}
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm"
                                  rows={2}
                                />
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={async () => {
                                      try {
                                        await updateDoc(doc(db, 'walletTransactions', tx.id), {
                                          description: editingDescription.text.trim() || null
                                        });
                                        
                                        await logAdminAction('transaction.update_description', {
                                          transactionId: tx.transaction_id,
                                          oldDescription: tx.description || '',
                                          newDescription: editingDescription.text.trim() || ''
                                        });
                                        
                                        setEditingDescription(null);
                                        fetchUserTransactions(selectedUser.user_id);
                                        toast.success('Megjegyzés módosítva');
                                      } catch (error) {
                                        console.error('Error updating description:', error);
                                        toast.error('Hiba történt a megjegyzés módosítása során');
                                      }
                                    }}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <Icons.Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingDescription(null)}
                                    className="text-gray-400 hover:text-gray-500"
                                  >
                                    <Icons.X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="group flex items-start gap-2">
                                <span className="flex-1">{tx.description || '-'}</span>
                                <button
                                  onClick={() => setEditingDescription({
                                    id: tx.id,
                                    text: tx.description || ''
                                  })}
                                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600"
                                >
                                  <Icons.Edit2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tx.created_at?.toDate?.().toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {!tx.reverted && (
                              <button
                                onClick={() => handleRevertTransaction(tx)}
                                className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                              >
                                <Icons.RotateCcw className="h-4 w-4" />
                                <span>Visszavonás</span>
                              </button>
                            )}
                            {tx.reverted && (
                              <span className="text-gray-500 text-xs">Visszavonva</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-center">
              {hasMoreTransactions && (
                <button
                  onClick={() => fetchUserTransactions(selectedUser.user_id, transactionPage + 1)}
                  disabled={loadingTransactions}
                  className="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md flex items-center gap-2"
                >
                  {loadingTransactions ? (
                    <Icons.Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Icons.ChevronDown className="h-4 w-4" />
                  )}
                  További tranzakciók betöltése
                </button>
              )}
              <div className="text-sm text-gray-500 ml-4">
                {userTransactions.length} tranzakció betöltve
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedUsers.length > 0 && (
        <BatchOperationsManager
          selectedItems={selectedUsers}
          type="users"
          onComplete={() => {
            setSelectedUsers([]);
            fetchWalletUsers();
          }}
        />
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-6xl w-full">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-medium text-gray-900">Új utalás</h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Címzett
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Keresés email alapján..."
                    value={recipientSearch}
                    onChange={(e) => {
                      setRecipientSearch(e.target.value);
                      searchRecipients(e.target.value);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {recipientResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {recipientResults.map(user => (
                        <button
                          key={user.id}
                          onClick={() => {
                            setSelectedRecipient(user);
                            setRecipientSearch(user.email);
                            setRecipientResults([]);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50"
                        >
                          <div className="text-sm font-medium">{user.email}</div>
                          <div className="text-xs text-gray-500">
                            Egyenleg: {user.details?.balances?.[selectedCoin] || 0} {selectedCoin}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coin
                  </label>
                  <select
                    value={selectedCoin}
                    onChange={(e) => setSelectedCoin(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {Object.keys(selectedUser.details?.balances || {}).map(coin => (
                      <option key={coin} value={coin}>{coin}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Összeg
                  </label>
                  <input
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-700 font-medium">
                  Elérhető egyenleg: <span className="text-indigo-600">{selectedUser.details?.balances?.[selectedCoin] || 0} {selectedCoin}</span>
                </div>
                {selectedRecipient && (
                  <div className="mt-2 text-sm text-gray-700">
                    <span className="font-medium">Címzett egyenlege:</span> <span className="text-indigo-600">{selectedRecipient.details?.balances?.[selectedCoin] || 0} {selectedCoin}</span>
                  </div>
                )}
              </div>

              {/* Megjegyzés mező */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Megjegyzés
                </label>
                <textarea
                  value={transferDescription}
                  onChange={(e) => setTransferDescription(e.target.value)}
                  placeholder="Opcionális megjegyzés az utaláshoz..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="mr-4 px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Mégsem
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={!selectedRecipient || !transferAmount}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  Utalás Végrehajtása
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBalanceModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full">
            <AddWalletBalance
              key={selectedUser.id}
              user={selectedUser}
              onClose={() => {
                setShowBalanceModal(false);
                setSelectedUser(null);
              }}
              onSuccess={() => {
                fetchWalletUsers();
                setShowBalanceModal(false);
                setSelectedUser(null);
              }}
            />
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full">
            <CreateWalletUser
              onClose={() => setShowCreateModal(false)}
              onSuccess={() => {
                fetchWalletUsers();
                setShowCreateModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
} 